import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useSetAtom } from "jotai";

import {
  createErrorResultFromException,
  IReceiptWithSource,
  receiptAtom,
  receiptErrorAtom,
  receiptLoadingAtom,
} from "@prostocheck/shared";

import { fetchReceipt } from "../../api";
import { notifyError } from "../../notifications";
import { useSession } from "../use-session";
import { IUseReceipt } from "./use-receipt.models";

/**
 * Fetches one receipt and writes it into `receiptAtom` — nothing else. Its
 * own loading/error pair (`receiptLoadingAtom`/`receiptErrorAtom`), not the
 * generic ones: the receipt modal can be opened while the register page's
 * own receipts/Z-reports fetches are still in flight, and a shared flag would
 * flip between them.
 */
export const useReceipt = (): IUseReceipt => {
  const { t } = useTranslation();
  const { session } = useSession();
  const setReceiptWithSource = useSetAtom(receiptAtom);
  const setIsLoading = useSetAtom(receiptLoadingAtom);
  const setErrorResult = useSetAtom(receiptErrorAtom);

  const getReceipt = useCallback(
    async (cashRegisterFn: string, fiscalNumber: string) => {
      setIsLoading(true);
      setErrorResult(null);

      try {
        // `useSession` already guarantees a session inside RequireSession's routes.
        const result = await fetchReceipt({
          client: session!.client,
          cashRegisterFn,
          fiscalNumber,
        });

        if (!result.success || !result.data) {
          setErrorResult(result);
          notifyError({ result, t });

          return;
        }

        setReceiptWithSource(result.data);
      } catch (error) {
        const result = createErrorResultFromException<IReceiptWithSource>(error);

        setErrorResult(result);
        notifyError({ result, t });
      } finally {
        setIsLoading(false);
      }
    },
    [session, t, setIsLoading, setErrorResult, setReceiptWithSource],
  );

  return { getReceipt };
};
